import { useEffect, useState } from "react";
import {
  useNotificationSettings,
  useSaveSettings,
  useTestNotification,
  useRunScan,
  useNotifications,
  useEmailStatus,
  useEmailTest,
  TRIGGER_LABELS,
  type NotificationSettings,
  type TriggerConf,
} from "../lib/notifications";
import { usePerspective } from "../lib/perspective";
import { useAuth } from "../lib/auth";
import "../styles.proactive.css";
import "../styles.w3.css";

/**
 * Einstellseite "Erinnerungen & Benachrichtigungen" (CompanyBrain Welle 2, BEN-106, W2.2).
 * Nutzer stellt Ausloeser, Kanaele und Frequenz ein. Persistent (Postgres, tenant+user-scoped).
 */
const TRIGGER_ORDER = [
  "kpi_threshold",
  "ressort_verzug",
  "wp_overdue",
  "process_open",
  "evidence_missing",
  "due_date",
  "new_run_evidence",
  "inactivity",
];

const FREQ: { key: NotificationSettings["frequency"]; label: string; hint: string }[] = [
  { key: "immediate", label: "Sofort", hint: "Sobald ein Ausloeser erkannt wird (taegliche Wiedervorlage)." },
  { key: "daily", label: "Taeglich", hint: "Einmal pro Tag gebuendelt." },
  { key: "weekly", label: "Woechentlich", hint: "Einmal pro Woche gebuendelt." },
];

export default function BenachrichtigungenPage() {
  const q = useNotificationSettings();
  const save = useSaveSettings();
  const test = useTestNotification();
  const scan = useRunScan();
  const list = useNotifications("all");
  const emailStatus = useEmailStatus();
  const emailTest = useEmailTest();
  const est = emailStatus.data;
  const { isBerater } = usePerspective();
  const { roles } = useAuth();
  const canScan = isBerater || roles.includes("platform_admin") || roles.includes("tenant_admin");

  const [form, setForm] = useState<NotificationSettings | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    if (q.data && !form) setForm(JSON.parse(JSON.stringify(q.data.settings)) as NotificationSettings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  if (q.isLoading || !form) {
    return <section className="nset"><p className="muted">Einstellungen werden geladen &hellip;</p></section>;
  }

  const persistent = q.data?.persistent ?? false;

  function up(patch: Partial<NotificationSettings>) {
    setForm((f) => (f ? { ...f, ...patch } : f));
    setSavedAt(null);
  }
  function upChannel(key: "inapp" | "email" | "chat", val: boolean) {
    setForm((f) => (f ? { ...f, channels: { ...f.channels, [key]: val } } : f));
    setSavedAt(null);
  }
  function upTrigger(key: string, patch: Partial<TriggerConf>) {
    setForm((f) =>
      f ? { ...f, triggers: { ...f.triggers, [key]: { ...(f.triggers[key] || { enabled: false }), ...patch } } } : f,
    );
    setSavedAt(null);
  }

  function onSave() {
    if (!form) return;
    const payload = { ...form, channels: { ...form.channels, inapp: true } };
    save.mutate(payload, { onSuccess: () => setSavedAt(new Date().toLocaleTimeString()) });
  }

  return (
    <section className="nset" aria-label="Erinnerungen und Benachrichtigungen">
      <header className="nset-hero">
        <div>
          <p className="hero-eyebrow">CompanyBrain &middot; Proaktive KI-Begleitung</p>
          <h1>Erinnerungen &amp; Benachrichtigungen</h1>
          <p className="muted">
            Legen Sie fest, <strong>wann</strong> die KI Sie proaktiv erinnert, <strong>worueber</strong> (Ausloeser)
            und <strong>wie oft</strong>. Benachrichtigungen erscheinen im Glocken-Menue oben; die Einstellungen gelten
            fuer Ihren Zugang.
          </p>
        </div>
      </header>

      {!persistent && (
        <p className="nset-warn small">
          Hinweis: Die persistente Speicherung ist derzeit nicht aktiv (Datenbank nicht verbunden) &mdash; Einstellungen
          wirken nur fuer diese Sitzung.
        </p>
      )}

      <div className="nset-grid">
        <div className="nset-card">
          <div className="nset-row">
            <label className="nset-switch">
              <input type="checkbox" checked={form.enabled} onChange={(e) => up({ enabled: e.target.checked })} />
              <span><strong>Erinnerungen aktiv</strong><small>Der Reminder-Job wertet Ihre Ausloeser periodisch aus.</small></span>
            </label>
          </div>

          <h3 className="nset-h3">Frequenz</h3>
          <div className="nset-freq">
            {FREQ.map((f) => (
              <label key={f.key} className={`nset-freq-opt ${form.frequency === f.key ? "active" : ""}`} title={f.hint}>
                <input type="radio" name="freq" checked={form.frequency === f.key} onChange={() => up({ frequency: f.key })} />
                <span><strong>{f.label}</strong><small>{f.hint}</small></span>
              </label>
            ))}
          </div>

          <h3 className="nset-h3">Kanaele</h3>
          <div className="nset-channels">
            <label className="nset-chk locked" title="Das Benachrichtigungszentrum (Glocke) ist immer aktiv.">
              <input type="checkbox" checked readOnly />
              <span>In-App (Glocke &amp; Badge) <em className="nset-tag">immer aktiv</em></span>
            </label>
            <label className="nset-chk">
              <input type="checkbox" checked={form.channels.email} onChange={(e) => upChannel("email", e.target.checked)} />
              <span>E-Mail{" "}
                {est ? (
                  <em className={`nset-tag ${est.state === "aktiv" ? "active" : est.state === "wartet_auf_smtp" ? "waiting" : "plan"}`}>
                    {est.state === "aktiv" ? "aktiv" : est.state === "wartet_auf_smtp" ? "wartet auf SMTP" : "geplant"}
                  </em>
                ) : <em className="nset-tag plan">&hellip;</em>}
              </span>
            </label>
            {form.channels.email && (
              <input
                className="nset-input"
                type="email"
                placeholder="E-Mail-Adresse fuer Benachrichtigungen"
                value={form.email_address}
                onChange={(e) => up({ email_address: e.target.value })}
              />
            )}
            {form.channels.email && est && (
              <div className="w3-email-status small">
                <div>{est.detail}</div>
                <div className="muted">Absender: {est.from}{est.default_to ? ` · Standard-Empfaenger: ${est.default_to}` : ""}</div>
                <button className="btn ghost small" type="button" onClick={() => emailTest.mutate()} disabled={emailTest.isPending}
                  title="Probelauf ohne echten Versand - belegt den Weg.">
                  {emailTest.isPending ? "Teste …" : "E-Mail-Weg testen (Probelauf)"}
                </button>
                {emailTest.data && <span className="nset-ok small"> Ergebnis: {emailTest.data.result}{emailTest.data.dry_run ? " (Probelauf, kein echter Versand)" : ""}</span>}
              </div>
            )}
            <label className="nset-chk">
              <input type="checkbox" checked={form.channels.chat} onChange={(e) => upChannel("chat", e.target.checked)} />
              <span>Chat (KIM) <em className="nset-tag plan">geplant</em></span>
            </label>
            <p className="muted small">
              <strong>E-Mail aktivieren:</strong> Der Weg ist vollstaendig gebaut. Sobald das SMTP-Passwort in der
              Secret-Datei auf dem Server hinterlegt und ein SMTP-Host konfiguriert ist, versendet der Kanal
              automatisch &mdash; ohne weiteres Zutun. Bis dahin ist er ehrlich als &bdquo;wartet auf SMTP&ldquo;
              markiert. Chat (KIM) folgt &mdash; In-App funktioniert immer sofort.
            </p>
          </div>
        </div>

        <div className="nset-card">
          <h3 className="nset-h3">Ausloeser</h3>
          <p className="muted small">Welche Situationen sollen eine Erinnerung erzeugen?</p>
          <div className="nset-triggers">
            {TRIGGER_ORDER.map((key) => {
              const t = form.triggers[key] || { enabled: false };
              return (
                <div key={key} className="nset-trg">
                  <label className="nset-chk">
                    <input type="checkbox" checked={!!t.enabled} onChange={(e) => upTrigger(key, { enabled: e.target.checked })} />
                    <span>{TRIGGER_LABELS[key] ?? key}</span>
                  </label>
                  {key === "kpi_threshold" && t.enabled && (
                    <div className="nset-sub">
                      Schwelle Zielerreichung unter
                      <input
                        className="nset-num"
                        type="number"
                        min={1}
                        max={100}
                        value={t.attainment_below_pct ?? 80}
                        onChange={(e) => upTrigger(key, { attainment_below_pct: Number(e.target.value) })}
                      />
                      %
                    </div>
                  )}
                  {key === "inactivity" && t.enabled && (
                    <div className="nset-sub">
                      nach
                      <input
                        className="nset-num"
                        type="number"
                        min={1}
                        max={90}
                        value={t.days ?? 7}
                        onChange={(e) => upTrigger(key, { days: Number(e.target.value) })}
                      />
                      Tagen
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="nset-actions">
        <button className="btn primary" onClick={onSave} disabled={save.isPending}>
          {save.isPending ? "Speichere …" : "Einstellungen speichern"}
        </button>
        <button className="btn ghost" onClick={() => test.mutate()} disabled={test.isPending}>
          {test.isPending ? "Sende …" : "Test-Benachrichtigung senden"}
        </button>
        {canScan && (
          <button className="btn ghost" onClick={() => scan.mutate()} disabled={scan.isPending} title="Wertet jetzt die Ausloeser aus und erzeugt faellige Benachrichtigungen.">
            {scan.isPending ? "Pruefe …" : "Jetzt pruefen (Scan)"}
          </button>
        )}
        {savedAt && <span className="nset-ok small">Gespeichert um {savedAt}.</span>}
        {test.isSuccess && <span className="nset-ok small">Test-Benachrichtigung angelegt &mdash; siehe Glocke oben.</span>}
        {scan.isSuccess && (
          <span className="nset-ok small">
            Scan ausgefuehrt: {scan.data?.created ?? 0} neue Benachrichtigung(en) aus {scan.data?.candidates ?? 0} Punkten.
          </span>
        )}
      </div>

      <div className="nset-recent">
        <h3 className="nset-h3">Zuletzt erzeugt</h3>
        {(list.data?.items ?? []).length === 0 ? (
          <p className="muted small">Noch keine Benachrichtigungen. Aktivieren Sie Ausloeser oder senden Sie eine Test-Benachrichtigung.</p>
        ) : (
          <ul className="nset-recent-list">
            {(list.data?.items ?? []).slice(0, 8).map((n) => (
              <li key={n.id} className={`nset-recent-item sev-${n.severity} ${n.read ? "read" : "unread"}`}>
                <strong>{n.title}</strong>
                <span className="muted small">{n.body}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
